import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Loader2,
  X,
  Sparkles,
  CheckSquare,
  CalendarClock,
  Briefcase,
  LifeBuoy,
  ShoppingCart,
  ExternalLink,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "../ui/sonner";
import DatePickerInput from "../DatePickerInput";
import VoiceTextarea from "../VoiceTextarea";

/**
 * ConvertMessageDialog — Wave 19 #94.
 *
 * Universal modal that converts a chat message into one of:
 *   - task         → POST /tasks
 *   - event        → POST /meetings
 *   - jobcard      → POST /jobcards (managers/admins only)
 *   - ticket       → POST /tickets
 *   - procurement  → POST /order-requests
 *
 * Strategy:
 *   1. Open instantly with dumb prefill (title=first 60 chars, full
 *      description, customer=chat thread customer).
 *   2. Background-fetch /chats/{tid}/messages/{mid}/extract to get
 *      Hidro AI suggestions (title, due date, priority, assignee).
 *   3. Render each suggestion as a "✨ Hidro suggests" pill the user
 *      taps to accept.
 *   4. On submit, include source_chat_thread_id + source_chat_message_id
 *      so the new entity carries a breadcrumb back to the source chat.
 *   5. On success, close the modal, fire a toast with a deep link to
 *      the new entity. The backend posts a system bubble in the source
 *      chat automatically.
 */

const META = {
  task: { label: "Task", Icon: CheckSquare, color: "#10B981", titleField: "title" },
  event: { label: "Event", Icon: CalendarClock, color: "#A78BFA", titleField: "title" },
  jobcard: { label: "Job card", Icon: Briefcase, color: "#F59E0B", titleField: "title" },
  ticket: { label: "Ticket", Icon: LifeBuoy, color: "#F43F5E", titleField: "subject" },
  procurement: { label: "Procurement order", Icon: ShoppingCart, color: "#1FB6C1", titleField: "name" },
};

function _smartTitle(text, maxLen = 60) {
  const s = (text || "").trim().split("\n")[0];
  if (s.length <= maxLen) return s;
  return s.slice(0, maxLen - 1).trim() + "…";
}

function _isoDateToday() {
  return new Date().toISOString().slice(0, 10);
}

function _addDays(isoDate, days) {
  const d = new Date(isoDate);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

export default function ConvertMessageDialog({ thread, message, target, onClose }) {
  const navigate = useNavigate();
  const meta = META[target] || META.task;
  const Icon = meta.Icon;
  const messageText = (message?.transcription || message?.text || "").trim();

  // Dumb prefill (instant).
  const [title, setTitle] = useState(() => _smartTitle(messageText) || `From chat — ${thread?.title || ""}`);
  const [description, setDescription] = useState(messageText);
  const [priority, setPriority] = useState("normal");
  const [dueDate, setDueDate] = useState("");
  const [assigneeId, setAssigneeId] = useState("");
  const [eventStart, setEventStart] = useState(() => {
    const d = new Date();
    d.setMinutes(d.getMinutes() + 30 - (d.getMinutes() % 15));
    return d.toISOString().slice(0, 16);
  });
  const [eventEnd, setEventEnd] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() + 1);
    d.setMinutes(d.getMinutes() + 30 - (d.getMinutes() % 15));
    return d.toISOString().slice(0, 16);
  });
  const [siteId, setSiteId] = useState("");
  const [orderQty, setOrderQty] = useState(1);
  const [busy, setBusy] = useState(false);

  // AI state
  const [aiLoading, setAiLoading] = useState(true);
  const [ai, setAi] = useState(null);

  // Staff + customer sites for assignee / site pickers
  const [staff, setStaff] = useState([]);
  const [sites, setSites] = useState([]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [{ data: u }, sitesRes] = await Promise.all([
          api.get("/users"),
          thread?.customer_id
            ? api.get(`/sites?customer_id=${thread.customer_id}`).catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
        ]);
        if (!alive) return;
        setStaff(Array.isArray(u) ? u.filter((x) => x.role !== "client") : []);
        const siteRows = Array.isArray(sitesRes?.data) ? sitesRes.data : [];
        setSites(siteRows);
        if (siteRows.length > 0) {
          setSiteId(siteRows[0].id);
        }
      } catch (_) {
        /* noop */
      }
    })();
    return () => {
      alive = false;
    };
  }, [thread?.customer_id]);

  // Background AI extraction — never blocks the form.
  useEffect(() => {
    if (!thread?.id || !message?.id) {
      setAiLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post(
          `/chats/${thread.id}/messages/${message.id}/extract`,
          {},
          { timeout: 8_000 },
        );
        if (!alive) return;
        setAi(data?.ai || {});
      } catch (_) {
        if (alive) setAi({});
      } finally {
        if (alive) setAiLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [thread?.id, message?.id]);

  const validation = useMemo(() => {
    if (!title.trim()) return "Title is required.";
    if (target === "event" && (!eventStart || !eventEnd)) return "Start & end times are required.";
    if (target === "ticket" && !thread?.customer_id) return "This chat isn't linked to a customer; cannot create a ticket.";
    if (target === "ticket" && !siteId) return "Pick a site for the ticket.";
    if (target === "jobcard" && (!thread?.customer_id || !siteId)) return "Job cards need a customer + site.";
    if (target === "procurement" && !thread?.customer_id) return "This chat isn't linked to a customer; cannot create an order.";
    return "";
  }, [title, target, eventStart, eventEnd, thread, siteId]);

  const submit = async () => {
    if (validation) {
      toast.error(validation);
      return;
    }
    setBusy(true);
    const sourceChat = {
      source_chat_thread_id: thread.id,
      source_chat_message_id: message.id,
    };
    try {
      let resp = null;
      let openLink = null;
      if (target === "task") {
        resp = await api.post("/tasks", {
          title: title.trim(),
          description,
          priority,
          due_date: dueDate || null,
          assigned_to_ids: assigneeId ? [assigneeId] : [],
          linked_customer_id: thread?.customer_id || null,
          ...sourceChat,
        });
        openLink = "/tasks";
      } else if (target === "event") {
        const startIso = new Date(eventStart).toISOString();
        const endIso = new Date(eventEnd).toISOString();
        resp = await api.post("/meetings", {
          title: title.trim(),
          description,
          customer_id: thread?.customer_id || null,
          start: startIso,
          end: endIso,
          attendees: assigneeId ? [assigneeId] : [],
          ...sourceChat,
        });
        openLink = "/calendar";
      } else if (target === "jobcard") {
        resp = await api.post("/jobcards", {
          customer_id: thread?.customer_id,
          site_id: siteId,
          title: title.trim(),
          description,
          phase: "picking",
          priority,
          technician_id: assigneeId || null,
          checklist: [],
          ...sourceChat,
        });
        openLink = `/jobcards/${resp.data.id}`;
      } else if (target === "ticket") {
        resp = await api.post("/tickets", {
          customer_id: thread.customer_id,
          site_id: siteId,
          subject: title.trim(),
          description,
          priority,
          assigned_to_id: assigneeId || null,
          status: "logged",
          ...sourceChat,
        });
        openLink = `/tickets/${resp.data.id}`;
      } else if (target === "procurement") {
        resp = await api.post("/order-requests", {
          customer_id: thread.customer_id,
          site_id: siteId || null,
          preferred_date: dueDate || null,
          notes: description,
          lines: [
            {
              name: title.trim(),
              quantity: Math.max(1, Number(orderQty) || 1),
              unit: "pcs",
              notes: description,
            },
          ],
          ...sourceChat,
        });
        openLink = `/orders/${resp.data.id}`;
      }
      const newId = resp?.data?.id;
      const shortId = resp?.data?.short_id;
      toast.success(
        `${meta.label} created${shortId ? ` · ${shortId}` : ""}`,
        {
          description: "Linked back to this chat.",
          duration: 6000,
          action: openLink && newId
            ? {
                label: "Open",
                onClick: () => navigate(openLink),
              }
            : undefined,
          // Sonner doesn't expose data-testid directly on the toast root,
          // but tests can grep `.toast` or wait for the success message.
        },
      );
      onClose?.({ created: true, target, id: newId });
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || `Could not create ${meta.label.toLowerCase()}.`,
      );
    } finally {
      setBusy(false);
    }
  };

  const acceptAiTitle = () => {
    if (ai?.title) setTitle(ai.title);
  };
  const acceptAiDue = () => {
    if (ai?.suggested_due_date) setDueDate(ai.suggested_due_date);
  };
  const acceptAiPriority = () => {
    if (ai?.suggested_priority) setPriority(ai.suggested_priority);
  };
  const acceptAiAssignee = () => {
    if (ai?.suggested_assignee_id) setAssigneeId(ai.suggested_assignee_id);
  };

  const showField = {
    priority: ["task", "jobcard", "ticket"].includes(target),
    dueDate: ["task", "procurement"].includes(target),
    assignee: ["task", "event", "jobcard", "ticket"].includes(target),
    eventTimes: target === "event",
    site: ["jobcard", "ticket", "procurement"].includes(target),
    qty: target === "procurement",
  };

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/75 backdrop-blur-sm flex items-end md:items-center justify-center p-0 md:p-6 hv-safe-top hv-safe-bottom hv-safe-x"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
      data-testid="convert-message-dialog"
    >
      <div
        className="w-full max-w-lg bg-[#121419] border border-white/10 rounded-t-2xl md:rounded-lg overflow-hidden flex flex-col max-h-[calc(100dvh-env(safe-area-inset-top)-env(safe-area-inset-bottom))]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div
              className="w-7 h-7 rounded-md border flex items-center justify-center shrink-0"
              style={{ borderColor: `${meta.color}55`, background: `${meta.color}1a` }}
            >
              <Icon className="w-3.5 h-3.5" style={{ color: meta.color }} />
            </div>
            <div>
              <h3 className="font-display text-base font-bold tracking-tight">
                Create {meta.label}
              </h3>
              <p className="text-[10px] text-[#647087]">
                From chat · {thread?.title || "—"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => onClose?.()}
            className="text-[#647087] hover:text-white transition p-1 rounded"
            data-testid="convert-dialog-close"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* AI status banner */}
          <div
            className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 rounded border"
            style={{
              color: aiLoading ? "#647087" : ai && Object.keys(ai).length ? "#1FB6C1" : "#647087",
              borderColor: aiLoading ? "#647087" : ai && Object.keys(ai).length ? "#1FB6C155" : "#647087",
              background: aiLoading || !(ai && Object.keys(ai).length) ? "transparent" : "#1FB6C112",
            }}
            data-testid="convert-ai-status"
          >
            {aiLoading ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>Hidro is reading the message…</span>
              </>
            ) : ai && Object.keys(ai).length ? (
              <>
                <Sparkles className="w-3 h-3" />
                <span>Hidro has suggestions — tap to apply</span>
              </>
            ) : (
              <>
                <Sparkles className="w-3 h-3 opacity-50" />
                <span className="opacity-70">No AI suggestions available</span>
              </>
            )}
          </div>

          {/* Title */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
              {target === "ticket" ? "Subject" : target === "procurement" ? "Item name" : "Title"}
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="hv-input mt-1 text-sm"
              data-testid="convert-title-input"
              autoFocus
              maxLength={140}
            />
            {ai?.title && ai.title !== title && (
              <button
                type="button"
                onClick={acceptAiTitle}
                className="text-[10px] mt-1 inline-flex items-center gap-1 text-[#1fb6c1] hover:text-white transition"
                data-testid="convert-ai-apply-title"
              >
                <Sparkles className="w-3 h-3" />
                <span className="truncate max-w-[280px]">Hidro: "{ai.title}"</span>
              </button>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
              {target === "procurement" ? "Notes" : "Description"}
            </label>
            <VoiceTextarea
              value={description}
              onChange={setDescription}
              data-testid="convert-description-input"
              rows={3}
             testIdPrefix="convert-description-input-voice" context="note" />
          </div>

          {/* Event start/end */}
          {showField.eventTimes && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Starts</label>
                <input
                  type="datetime-local"
                  value={eventStart}
                  onChange={(e) => setEventStart(e.target.value)}
                  className="hv-input mt-1 text-sm"
                  data-testid="convert-event-start"
                />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Ends</label>
                <input
                  type="datetime-local"
                  value={eventEnd}
                  onChange={(e) => setEventEnd(e.target.value)}
                  className="hv-input mt-1 text-sm"
                  data-testid="convert-event-end"
                />
              </div>
            </div>
          )}

          {/* Quantity */}
          {showField.qty && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Quantity</label>
              <input
                type="number"
                min={1}
                value={orderQty}
                onChange={(e) => setOrderQty(e.target.value)}
                className="hv-input mt-1 text-sm w-24"
                data-testid="convert-qty-input"
              />
            </div>
          )}

          {/* Site picker (jobcard/ticket/procurement) */}
          {showField.site && thread?.customer_id && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
                Site {target === "procurement" ? "(optional)" : ""}
              </label>
              <select
                value={siteId}
                onChange={(e) => setSiteId(e.target.value)}
                className="hv-input mt-1 text-sm"
                data-testid="convert-site-select"
              >
                <option value="">— Select site —</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>{s.title || s.name || s.address}</option>
                ))}
              </select>
              {sites.length === 0 && (
                <div className="text-[10px] text-amber-300 mt-1">
                  This customer has no sites — open the customer profile to add one first.
                </div>
              )}
            </div>
          )}

          {/* Priority + due-date row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {showField.priority && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Priority</label>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="hv-input mt-1 text-sm"
                  data-testid="convert-priority-select"
                >
                  <option value="low">Low</option>
                  <option value="normal">Normal</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
                {ai?.suggested_priority && ai.suggested_priority !== priority && (
                  <button
                    type="button"
                    onClick={acceptAiPriority}
                    className="text-[10px] mt-1 inline-flex items-center gap-1 text-[#1fb6c1] hover:text-white transition"
                    data-testid="convert-ai-apply-priority"
                  >
                    <Sparkles className="w-3 h-3" /> Hidro: {ai.suggested_priority}
                  </button>
                )}
              </div>
            )}
            {showField.dueDate && (
              <div>
                <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">Due date</label>
                <DatePickerInput
                  value={dueDate}
                  onChange={setDueDate}
                  testId="convert-due-input"
                />
                {ai?.suggested_due_date && ai.suggested_due_date !== dueDate && (
                  <button
                    type="button"
                    onClick={acceptAiDue}
                    className="text-[10px] mt-1 inline-flex items-center gap-1 text-[#1fb6c1] hover:text-white transition"
                    data-testid="convert-ai-apply-due"
                  >
                    <Sparkles className="w-3 h-3" /> Hidro: {ai.suggested_due_date}
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Assignee */}
          {showField.assignee && (
            <div>
              <label className="text-[10px] uppercase tracking-wider text-[#a0aabf]">
                {target === "event" ? "Attendee" : target === "jobcard" ? "Technician" : "Assignee"}
              </label>
              <select
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                className="hv-input mt-1 text-sm"
                data-testid="convert-assignee-select"
              >
                <option value="">— Select —</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>{s.name} · {s.role}</option>
                ))}
              </select>
              {ai?.suggested_assignee_id && ai.suggested_assignee_id !== assigneeId && (
                <button
                  type="button"
                  onClick={acceptAiAssignee}
                  className="text-[10px] mt-1 inline-flex items-center gap-1 text-[#1fb6c1] hover:text-white transition"
                  data-testid="convert-ai-apply-assignee"
                >
                  <Sparkles className="w-3 h-3" />
                  Hidro: {ai.suggested_assignee_name || "suggested user"}
                </button>
              )}
            </div>
          )}

          {/* Customer link summary (always visible) */}
          {thread?.customer_id && (
            <div className="text-[10px] text-[#647087] flex items-center gap-1.5 pt-1">
              <ExternalLink className="w-3 h-3" />
              <span>Auto-linked to customer:</span>
              <span className="text-[#cbd2df] font-medium truncate">
                {thread.customer_name || thread.customer_id}
              </span>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-white/10 flex items-center justify-between gap-3">
          <div className="text-[10px] text-rose-300 truncate" data-testid="convert-validation">
            {validation || ""}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => onClose?.()}
              className="hv-btn hv-btn-ghost text-xs"
              data-testid="convert-cancel-btn"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={submit}
              disabled={busy || !!validation}
              className="hv-btn hv-btn-primary text-xs"
              data-testid="convert-submit-btn"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : `Create ${meta.label.toLowerCase()}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
